import { useEffect, useState, useCallback, useRef } from 'react';
import webSocketService from '@services/websocket.service';
import { API_CONFIG } from '../config/api';

export interface UseWebSocketOptions {
  autoConnect?: boolean;
  events?: Record<string, (data: any) => void>;
}

export interface UseWebSocketReturn {
  connected: boolean;
  connecting: boolean;
  latency: number;
  connect: () => void;
  disconnect: () => void;
  reconnect: () => void;
  emit: (event: string, data: any) => void;
  on: (event: string, handler: (data: any) => void) => () => void;
}

export const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [latency, setLatency] = useState(0);
  const cleanupFnsRef = useRef<(() => void)[]>([]);
  const latencyIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(() => {
    if (connected || connecting) return;

    setConnecting(true);
    // Use centralized WebSocket URL configuration
    webSocketService.connect({
      url: API_CONFIG.WS_URL,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
    });

    const unsubscribe = webSocketService.onConnectionChange((isConnected) => {
      setConnected(isConnected);
      setConnecting(false);
    });

    cleanupFnsRef.current.push(unsubscribe);

    if (options.events) {
      Object.entries(options.events).forEach(([event, handler]) => {
        const unsubscribe = webSocketService.on(event, handler);
        cleanupFnsRef.current.push(unsubscribe);
      });
    }

    latencyIntervalRef.current = setInterval(() => {
      setLatency(webSocketService.getLatency());
    }, 1000);
  }, [connected, connecting, options.events]);

  const disconnect = useCallback(() => {
    webSocketService.disconnect();
    setConnected(false);
    setConnecting(false);

    if (latencyIntervalRef.current) {
      clearInterval(latencyIntervalRef.current);
      latencyIntervalRef.current = null;
    }

    cleanupFnsRef.current.forEach(cleanup => cleanup());
    cleanupFnsRef.current = [];
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    setTimeout(connect, 100);
  }, [connect, disconnect]);

  const emit = useCallback((event: string, data: any) => {
    webSocketService.emit(event, data);
  }, []);

  const on = useCallback((event: string, handler: (data: any) => void) => {
    const unsubscribe = webSocketService.on(event, handler);
    cleanupFnsRef.current.push(unsubscribe);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (options.autoConnect !== false) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- connects to an external system
      connect();
    }

    return () => {
      disconnect();
    };
  }, []);

  return {
    connected,
    connecting,
    latency,
    connect,
    disconnect,
    reconnect,
    emit,
    on,
  };
};
